import { toast } from 'sonner';

import { useRefetchOrderDocumentsMutation } from '@/graphql/mutations/refetchOrderDocuments.generated';
import i18n from '@/utils/i18n';

// Fetches both order documents (shipping label + delivery note) in one call and
// opens each returned PDF in a new tab.
export function useRefetchOrderDocuments() {
  const [refetchOrderDocuments, { loading }] =
    useRefetchOrderDocumentsMutation();

  const handleRefetchOrderDocuments = async (ids: string[]) => {
    let data: Awaited<ReturnType<typeof refetchOrderDocuments>>['data'];

    try {
      const response = await refetchOrderDocuments({ variables: { ids } });
      data = response.data;
    } catch {
      toast.error(i18n.t('Order:toastRefetchOrderDocumentsError'));
      return;
    }

    const result = data?.refetchOrderDocuments;
    // Open the delivery note first (the document most often missing), then the
    // shipping label. Each opens in its own tab. A document that failed does not
    // withhold the other one.
    if (result?.delivery_note_url) {
      window.open(result.delivery_note_url, '_blank');
    }
    if (result?.labels_url) {
      window.open(result.labels_url, '_blank');
    }
    const openedAny = !!(result?.delivery_note_url || result?.labels_url);
    if (!openedAny) {
      toast.error(i18n.t('Order:toastRefetchOrderDocumentsError'));
    } else if (result?.has_errors) {
      toast.warning(i18n.t('Order:toastRefetchOrderDocumentsPartial'));
    } else {
      toast.success(i18n.t('Order:toastRefetchOrderDocumentsSuccess'));
    }
  };

  return { handleRefetchOrderDocuments, refetchingOrderDocuments: loading };
}
